import { useState, useRef } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCreateTestCase } from '../../hooks/useTestCases';
import type { SpecListItem } from '../../services/specificationService';

const TYPE_OPTIONS = [
  { value: 'POSITIVE', label: 'Positive' },
  { value: 'NEGATIVE', label: 'Negative' },
  { value: 'BOUNDARY', label: 'Boundary' },
  { value: 'ALTERNATIVE', label: 'Alternative' },
] as const;

const PRIORITY_OPTIONS = [
  { value: 'BLOCKER', label: '블로커' },
  { value: 'CRITICAL', label: '긴급' },
  { value: 'HIGH', label: '높음' },
  { value: 'MEDIUM', label: '보통' },
  { value: 'LOW', label: '낮음' },
] as const;

interface Step {
  step: number;
  action: string;
}

interface Props {
  projectId: string;
  specs: SpecListItem[];
  onClose: () => void;
}

export default function ManualTCCreateModal({ projectId, specs, onClose }: Props) {
  const createMutation = useCreateTestCase(projectId);

  const [specId, setSpecId] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<string>('POSITIVE');
  const [priority, setPriority] = useState<string>('MEDIUM');
  const [precondition, setPrecondition] = useState('');
  const [steps, setSteps] = useState<Step[]>([{ step: 1, action: '' }]);
  const [expectedResult, setExpectedResult] = useState('');
  const stepInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const addStep = () => {
    setSteps((prev) => [...prev, { step: prev.length + 1, action: '' }]);
  };

  const removeStep = (index: number) => {
    if (steps.length <= 1) return;
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((s, i) => ({ ...s, step: i + 1 }));
    });
  };

  const updateStep = (index: number, value: string) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, action: value } : s)));
  };

  const handleStepKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const nextIdx = idx + 1;
    if (nextIdx < steps.length && !steps[nextIdx].action.trim()) {
      stepInputRefs.current[nextIdx]?.focus();
    } else {
      setSteps((prev) => [...prev, { step: prev.length + 1, action: '' }]);
      requestAnimationFrame(() => {
        stepInputRefs.current[nextIdx]?.focus();
      });
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('제목을 입력해주세요.');
      return;
    }
    if (steps.some((s) => !s.action.trim())) {
      toast.error('모든 테스트 단계의 동작을 입력해주세요.');
      return;
    }
    if (!expectedResult.trim()) {
      toast.error('기대 결과를 입력해주세요.');
      return;
    }

    try {
      await createMutation.mutateAsync({
        specId: specId || undefined,
        title: title.trim(),
        type: type as 'POSITIVE' | 'NEGATIVE' | 'BOUNDARY' | 'ALTERNATIVE',
        priority: priority as 'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW',
        precondition: precondition.trim() || undefined,
        steps: steps.map((s) => ({ step: s.step, action: s.action.trim() })),
        expectedResult: expectedResult.trim(),
      });
      toast.success('테스트케이스가 생성되었습니다.');
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '테스트케이스 생성에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">테스트케이스 수동 추가</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="size-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* 연결 명세 (선택) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">연결 명세 (선택)</label>
            <select
              value={specId}
              onChange={(e) => setSpecId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
            >
              <option value="">없음</option>
              {specs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.functionCode} {s.title}
                </option>
              ))}
            </select>
          </div>

          {/* 제목 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              제목 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="테스트케이스 제목"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
            />
          </div>

          {/* 유형 + 우선순위 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                유형 <span className="text-red-500">*</span>
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                우선순위 <span className="text-red-500">*</span>
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 사전조건 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">사전조건</label>
            <textarea
              value={precondition}
              onChange={(e) => setPrecondition(e.target.value)}
              rows={2}
              placeholder="테스트 실행 전 갖춰야 할 조건"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-none"
            />
          </div>

          {/* 테스트 단계 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                테스트 단계 <span className="text-red-500">*</span>
              </label>
              <button
                onClick={addStep}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
              >
                <Plus className="size-3" />
                단계 추가
              </button>
            </div>
            <div className="space-y-2">
              {steps.map((step, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-gray-50 rounded-lg p-3">
                  <span className="mt-2 text-sm font-mono text-gray-400 w-6 flex-shrink-0 text-center">
                    {step.step}
                  </span>
                  <input
                    ref={(el) => { stepInputRefs.current[idx] = el; }}
                    type="text"
                    value={step.action}
                    onChange={(e) => updateStep(idx, e.target.value)}
                    onKeyDown={(e) => handleStepKeyDown(e, idx)}
                    placeholder="동작 (Action)"
                    className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                  <button
                    onClick={() => removeStep(idx)}
                    disabled={steps.length <= 1}
                    className="mt-2 p-1 rounded hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="size-3.5 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 기대 결과 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              기대 결과 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={expectedResult}
              onChange={(e) => setExpectedResult(e.target.value)}
              rows={3}
              placeholder="전체 테스트의 기대 결과를 입력하세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? '생성 중...' : '생성'}
          </button>
        </div>
      </div>
    </div>
  );
}
